import axios from 'axios';
import { store } from '../store';
import { router } from '../router';


// Lazy load AuthAPIService to avoid circular import risk
let cachedAuth = null;
function getAuthAPIService() {
  if (!cachedAuth) {
    cachedAuth = require('./AuthAPIService').default;
  }
  return cachedAuth;
}
// Central error handler — used across all API methods and axios interceptors.
// Handles 401s (e.g., timeout), suppresses expected, rethrows others.
function handleError(e) {
  if (!e || !e.response) {
    return Promise.reject(e);
  }
  try {
    // Extract status and data once, guard against undefined
    const status = e.response.status;
    const errorData = e.response.data;

    if (status === 401) {
      const isCrossTabLogout = store.state.loggedIn === false;

      if (isCrossTabLogout && process.env.NODE_ENV === 'development') {
        return; // Don't rethrow — error is expected
      }

      if (errorData && errorData.status === 'TIMED_OUT') {
        console.debug('Session timed out, redirecting to /idle...');
        const current = router.currentRoute.path;
        if (current !== '/idle' && current !== '/login') {
          setTimeout(() => router.replace('/idle'), 0);// Skip race
        }
        return;
      }

      if (store.state.loggedIn) {
        console.debug('Logging out user due to 401...');
        store.dispatch('LOGOUT');
        const current = router.currentRoute.path;
        if (current !== '/login') {
          router.replace({ name: 'Login' }); // Named route to avoid back stack
        } else {
          console.debug('Already on /login, no redirect.');
        }
      }

      return; // Don't rethrow — handled 401s gracefully
    }
  } catch (err) {
    console.warn('handleError threw:', err); // Fail-safe logging
  }

  // Always propagate non-401 errors or malformed error objects
  return Promise.reject(e);
}

// Reset activity timer for all outgoing requests unless already timed out
axios.interceptors.request.use(
  (config) => {
    if (!store.state.timedOut) {
      store.dispatch('RESET_ACTIVE_TIMER');
    }
    return config;
  },
  (error) => Promise.reject(error)
);
// Global response handler for catching 401s (e.g. logout, session expiration)
axios.interceptors.response.use(
  response => response,
  error => {
    try {
      return handleError(error); // Returns undefined if 401 handled
    } catch (e) {
      console.warn('handleError threw in response interceptor:', e);
    }
    return Promise.reject(error);
  }
);

export class CommonAPIService {
  constructor() {
    this.api_url = process.env.VUE_APP_API_URL;
  }

  postCall(url, parameters, router) {
    const fullUrl = this.api_url + url;
    return axios.post(fullUrl, parameters, { withCredentials: true })
      .then(response => response.data)
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }

  fileCall(url, files, router) {
    const fullUrl = this.api_url + url;
    return axios.post(fullUrl, files,
      {
        headers: {'Content-Type': 'multipart/form-data'},
        withCredentials: true
      })
      .then(response => response)
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }

  deleteCall(url, router) {
    const fullUrl = this.api_url + url;
    return axios.delete(fullUrl, { withCredentials: true })
      .then(response => response.data)
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }

  putCall(url, parameters, router) {
    const fullUrl = this.api_url + url;
    return axios.put(fullUrl, parameters, { withCredentials: true })
      .then(response => response.data)
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }

  getCall(url, router) {
    const fullUrl = this.api_url + url;
    return axios.get(fullUrl, { withCredentials: true })
      .then(response => response.data)
      .catch((error) => {
        return handleError(error); // Handles + returns rejection if needed
      });
  }
}

export default CommonAPIService;
